import { Platform, DeviceEventEmitter } from 'react-native';
import Constants from 'expo-constants';
import Purchases, { LOG_LEVEL, PurchasesPackage, CustomerInfo, PurchasesOfferings } from 'react-native-purchases';
import RevenueCatUI, { PAYWALL_RESULT } from 'react-native-purchases-ui';
import { OfferingMetadata } from '../types/revenueCat';
const { safeLog } = require('../utils/safeLog.js');

/**
 * RevenueCat Service
 * 
 * Handles initialization and configuration of RevenueCat SDK for in-app purchases
 * and subscriptions. Initialized asynchronously to avoid blocking app startup.
 * 
 * Features:
 * - Platform-specific API key configuration (iOS/Android)
 * - Non-blocking async initialization
 * - Debug logging in development
 * - Error handling with graceful fallback
 * - User identification for analytics
 */

/**
 * Phase 1.5 (`subscription_purchase_started` + `subscription_package_selected`
 * via `Purchases.addPurchasesStartedListener`) is **deferred**: the API does
 * not exist on `react-native-purchases@9.6.10` (the version we ship — the
 * listener was added in 9.13.x). Re-enabling it requires a SDK upgrade plus
 * a native rebuild + regression on the subscription flows. Tracked in
 * `documentation/00. BACKLOG.md` § Analytics — Clarity Tagging.
 *
 * Until then the funnel collapses to
 * `paywall_viewed → purchase_succeeded | purchase_failed` and the typed
 * helpers `logSubscriptionPurchaseStarted` / `logSubscriptionPackageSelected`
 * exist but are intentionally never called from production code paths.
 */

class RevenueCatService {
  private isInitialized = false;
  private initPromise: Promise<void> | null = null;
  private customerInfoListener: ((customerInfo: CustomerInfo) => void) | null = null;

  /**
   * Initialize RevenueCat SDK
   * Safe to call multiple times - returns existing promise if already initializing
   */
  async initialize(): Promise<void> {
    // Return existing initialization promise if already in progress
    if (this.initPromise) {
      return this.initPromise;
    }

    // Already initialized
    if (this.isInitialized) {
      safeLog.info('💰 RevenueCat: Already initialized');
      return;
    }

    // Create new initialization promise
    this.initPromise = this._performInitialization();
    return this.initPromise;
  }

  private async _performInitialization(): Promise<void> {
    try {
      safeLog.info('💰 RevenueCat: Starting initialization...');

      // Get platform-specific API key from environment or config
      // Try process.env first (works in dev), then fall back to Constants.expoConfig.extra
      const apiKey = Platform.select({
        ios: process.env.EXPO_PUBLIC_REVENUECAT_IOS_KEY || Constants.expoConfig?.extra?.revenueCat?.iosApiKey,
        android: process.env.EXPO_PUBLIC_REVENUECAT_ANDROID_KEY || Constants.expoConfig?.extra?.revenueCat?.androidApiKey,
      });

      if (!apiKey) {
        safeLog.warn('⚠️ RevenueCat: API key not configured for', Platform.OS);
        safeLog.warn('💡 RevenueCat: Add EXPO_PUBLIC_REVENUECAT_IOS_KEY and EXPO_PUBLIC_REVENUECAT_ANDROID_KEY to your Replit Secrets');
        return;
      }

      // Configure RevenueCat
      const configuration = {
        apiKey,
        observerMode: false, // We handle purchases through RevenueCat
        useAmazon: false,
      };

      // Enable debug logs in development
      if (__DEV__) {
        Purchases.setLogLevel(LOG_LEVEL.DEBUG);
        safeLog.info('💰 RevenueCat: Debug logging enabled');
      } else {
        Purchases.setLogLevel(LOG_LEVEL.INFO);
      }

      // Configure SDK
      await Purchases.configure(configuration);
      
      // Set up customer info update listener for real-time subscription changes
      this.setupCustomerInfoListener();

      // Phase 1.5 (purchases-started listener) deferred — see top-of-file
      // comment. Re-enable once the RC SDK is upgraded to ≥ 9.13.x.

      this.isInitialized = true;
      safeLog.info('✅ RevenueCat: Initialization complete', {
        platform: Platform.OS,
        debugMode: __DEV__,
      });

    } catch (error) {
      safeLog.error('❌ RevenueCat: Initialization failed:', error);
      // Don't throw - allow app to continue without RevenueCat
      // Purchases will fail gracefully if SDK not initialized
    } finally {
      this.initPromise = null;
    }
  }

  /**
   * Set up listener for real-time customer info updates
   * Fires automatically when subscription status changes (upgrade, downgrade, cancel, restore)
   * Emits global events that UI components can listen to for instant updates
   */
  private setupCustomerInfoListener(): void {
    safeLog.info('👂 RevenueCat: Setting up customer info listener...');
    
    this.customerInfoListener = (customerInfo: CustomerInfo) => {
      safeLog.info('🔔 RevenueCat: Customer info updated!', {
        activeEntitlements: Object.keys(customerInfo.entitlements.active),
        timestamp: new Date().toISOString(),
      });

      // Check if Padrinho Plus status changed
      const hasPadrinhoPlus = customerInfo.entitlements.active['Padrinho Plus'] !== undefined;
      
      // Emit global event for UI components to react
      DeviceEventEmitter.emit('revenuecat:customer-info-updated', {
        customerInfo,
        hasPadrinhoPlus,
      });
      
      safeLog.info('📢 Emitted revenuecat:customer-info-updated event', { hasPadrinhoPlus });
    };

    // Add the listener to RevenueCat SDK
    Purchases.addCustomerInfoUpdateListener(this.customerInfoListener);
    safeLog.info('✅ RevenueCat: Customer info listener active');
  }

  /**
   * Remove customer info listener (cleanup)
   */
  private removeCustomerInfoListener(): void {
    if (this.customerInfoListener) {
      Purchases.removeCustomerInfoUpdateListener(this.customerInfoListener);
      this.customerInfoListener = null;
      safeLog.info('🔇 RevenueCat: Customer info listener removed');
    }
  }

  /**
   * Identify user for RevenueCat analytics
   * Call after user authentication
   */
  async identifyUser(userId: string): Promise<void> {
    // Race-condition fix: the auth listener can fire `SIGNED_IN` /
    // `INITIAL_SESSION` BEFORE the RevenueCat SDK has finished its async
    // `Purchases.configure()` step. The previous version early-returned
    // with a warning, which silently left the SDK on the anonymous
    // device user — every entitlement query then resolved against
    // `$RCAnonymousID:…` and a real Plus account looked Free.
    //
    // Wait for init to finish (or trigger it if it hasn't started) so
    // `Purchases.logIn` always runs against an initialized SDK. After
    // logIn the SDK fires a customer-info update which `usePadrinhoPlus`
    // now listens to, so screens that mounted earlier auto-refresh.
    if (!this.isInitialized) {
      try {
        await this.initialize();
      } catch (err) {
        safeLog.warn('⚠️ RevenueCat: initialize() during identifyUser failed:', err);
      }
    }

    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot identify user - SDK still not initialized after wait');
      return;
    }

    try {
      await Purchases.logIn(userId);
      safeLog.info('✅ RevenueCat: User identified:', userId);
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to identify user:', error);
    }
  }

  /**
   * Log out current user
   * Call when user signs out
   */
  async logoutUser(): Promise<void> {
    if (!this.isInitialized) {
      return;
    }

    try {
      await Purchases.logOut();
      safeLog.info('✅ RevenueCat: User logged out');
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to log out user:', error);
    }
  }

  /**
   * Get available subscription offerings with metadata
   */
  async getOfferings(): Promise<{
    offerings: PurchasesOfferings | null;
    current: {
      packages: PurchasesPackage[];
      metadata: OfferingMetadata;
    } | null;
  }> {
    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot get offerings - SDK not initialized');
      return { offerings: null, current: null };
    }

    try {
      const offerings = await Purchases.getOfferings();
      
      if (offerings.current !== null) {
        const packages = offerings.current.availablePackages;
        const metadata = (offerings.current as any).metadata || {};
        
        safeLog.info('💰 RevenueCat: Available packages:', packages.length);
        safeLog.info('💰 RevenueCat: Offering metadata:', metadata);
        
        return {
          offerings,
          current: {
            packages,
            metadata: metadata as OfferingMetadata,
          },
        };
      } else {
        safeLog.info('💰 RevenueCat: No current offering configured');
        return { offerings, current: null };
      }
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to get offerings:', error);
      return { offerings: null, current: null };
    }
  }

  /**
   * Purchase a package
   */
  async purchasePackage(pkg: PurchasesPackage): Promise<{ success: boolean; customerInfo?: CustomerInfo }> {
    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot purchase - SDK not initialized');
      return { success: false };
    }

    try {
      const { customerInfo } = await Purchases.purchasePackage(pkg);
      safeLog.info('✅ RevenueCat: Purchase successful');
      return { success: true, customerInfo };
    } catch (error: any) {
      if (error.userCancelled) {
        safeLog.info('💰 RevenueCat: User cancelled purchase');
      } else {
        safeLog.error('❌ RevenueCat: Purchase failed:', error);
      }
      return { success: false };
    }
  }

  /**
   * Get customer info (entitlements, subscriptions)
   */
  async getCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot get customer info - SDK not initialized');
      return null;
    }

    try {
      const customerInfo = await Purchases.getCustomerInfo();
      safeLog.info('💰 RevenueCat: Customer info retrieved', {
        activeSubscriptions: Object.keys(customerInfo.entitlements.active).length,
      });
      return customerInfo;
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to get customer info:', error);
      return null;
    }
  }

  /**
   * Force refresh customer info by invalidating cache first
   * Use this after subscription changes to ensure fresh data
   */
  async refreshCustomerInfo(): Promise<CustomerInfo | null> {
    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot refresh customer info - SDK not initialized');
      return null;
    }

    try {
      safeLog.info('💰 RevenueCat: Invalidating cache and refreshing customer info...');
      await Purchases.invalidateCustomerInfoCache();
      const customerInfo = await Purchases.getCustomerInfo();
      safeLog.info('💰 RevenueCat: Fresh customer info retrieved', {
        activeSubscriptions: Object.keys(customerInfo.entitlements.active).length,
        activeEntitlements: Object.keys(customerInfo.entitlements.active),
      });
      return customerInfo;
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to refresh customer info:', error);
      return null;
    }
  }

  /**
   * Restore purchases. Tagged for the Clarity micro `subscription_restore_tapped`
   * (PRD §5.12) — fires `tapped` on entry and resolves to `success`/`failed`
   * so the dashboard sees both intent and outcome from the explicit Restore
   * button (the native RC paywall's own Restore button is invisible to JS
   * and is still uninstrumented).
   */
  async restorePurchases(): Promise<{ success: boolean; customerInfo?: CustomerInfo }> {
    import('@/utils/clarityEvents')
      .then(({ logSubscriptionRestoreTapped }) =>
        logSubscriptionRestoreTapped({ result: 'tapped' })
      )
      .catch(() => {});

    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot restore purchases - SDK not initialized');
      import('@/utils/clarityEvents')
        .then(({ logSubscriptionRestoreTapped }) =>
          logSubscriptionRestoreTapped({ result: 'failed' })
        )
        .catch(() => {});
      return { success: false };
    }

    try {
      const customerInfo = await Purchases.restorePurchases();
      safeLog.info('✅ RevenueCat: Purchases restored');
      import('@/utils/clarityEvents')
        .then(({ logSubscriptionRestoreTapped }) =>
          logSubscriptionRestoreTapped({ result: 'success' })
        )
        .catch(() => {});
      return { success: true, customerInfo };
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to restore purchases:', error);
      import('@/utils/clarityEvents')
        .then(({ logSubscriptionRestoreTapped }) =>
          logSubscriptionRestoreTapped({ result: 'failed' })
        )
        .catch(() => {});
      return { success: false };
    }
  }

  /**
   * Check if user has active entitlement
   */
  async hasActiveEntitlement(entitlementId: string): Promise<boolean> {
    const customerInfo = await this.getCustomerInfo();
    if (!customerInfo) {
      return false;
    }

    return customerInfo.entitlements.active[entitlementId] !== undefined;
  }

  /**
   * Present RevenueCat Paywall
   * Shows a pre-built paywall UI for purchasing subscriptions
   * Uses the default offering configured in RevenueCat dashboard
   * 
   * @returns Result indicating if user made a purchase, cancelled, or error occurred
   */
  async presentPaywall(opts?: { fromSurface?: string }): Promise<{
    result: PAYWALL_RESULT;
    customerInfo: CustomerInfo | null;
  }> {
    const fromSurface = opts?.fromSurface ?? 'unknown';

    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot present paywall - SDK not initialized');
      return { result: PAYWALL_RESULT.ERROR, customerInfo: null };
    }

    // PRD §4 — `subscription_paywall_viewed` macro event. is_plus is false by
    // construction: the paywall would not be shown if the user already had
    // Plus. RESTORED outcomes (rare) self-correct via the success event below.
    // Also fires the PRD §6 nano `ui_action` with `element: 'plus_upgrade_cta'`
    // and `surface: <fromSurface>` so dashboards can filter recordings by
    // which screen's upgrade CTA the user tapped. All seven `presentPaywall`
    // callers in the codebase are inside tap handlers, so capturing at the
    // service level is correct and avoids per-call-site noise.
    const startedAt = Date.now();
    import('@/utils/clarityEvents')
      .then(({ logSubscriptionPaywallViewed, logUiAction }) => {
        logSubscriptionPaywallViewed({ from_surface: fromSurface, is_plus: false });
        logUiAction({ surface: fromSurface, element: 'plus_upgrade_cta' });
      })
      .catch(() => {});

    try {
      safeLog.info('💰 RevenueCat: Presenting paywall with default offering...');

      const paywallResult = await RevenueCatUI.presentPaywall();

      safeLog.info('✅ RevenueCat: Paywall result:', PAYWALL_RESULT[paywallResult]);

      // Get updated customer info after paywall closes
      const customerInfo = await this.getCustomerInfo();

      // PRD §4 — purchase outcome events.
      const isSuccess =
        paywallResult === PAYWALL_RESULT.PURCHASED || paywallResult === PAYWALL_RESULT.RESTORED;
      const isError = paywallResult === PAYWALL_RESULT.ERROR;
      const isCancelled = paywallResult === PAYWALL_RESULT.CANCELLED;

      if (isSuccess) {
        import('@/utils/clarityEvents')
          .then(({ logSubscriptionPurchaseSucceeded }) =>
            logSubscriptionPurchaseSucceeded({
              from_surface: fromSurface,
              duration_ms: Date.now() - startedAt,
            })
          )
          .catch(() => {});

        // Google Analytics `purchase` event is handled server-side by the
        // RevenueCat → Google Analytics integration (configured May 2026).
        // RevenueCat sends the event via Measurement Protocol with real price
        // data, so no client-side logPurchase() call is needed here.
        // DO NOT add a client-side call — it would double-count in Google Ads.
      } else if (isError || isCancelled) {
        import('@/utils/clarityEvents')
          .then(({ logSubscriptionPurchaseFailed }) =>
            logSubscriptionPurchaseFailed({
              error_code: isCancelled ? 'user_cancelled' : 'rc_error',
              from_surface: fromSurface,
            })
          )
          .catch(() => {});

        // PRD §5.12 — `subscription_paywall_dismissed` micro event. Fires
        // ONLY on user-initiated cancellation (not on RC errors — those are
        // system failures, not dismissals). Complementary signal to the
        // macro above: the macro lives in the purchase funnel, this micro
        // measures dismissal volume across all surfaces.
        if (isCancelled) {
          import('@/utils/clarityEvents')
            .then(({ logSubscriptionPaywallDismissed }) =>
              logSubscriptionPaywallDismissed({ from_surface: fromSurface })
            )
            .catch(() => {});
        }
      }
      // PAYWALL_RESULT.NOT_PRESENTED is a no-op — emits nothing.

      return {
        result: paywallResult,
        customerInfo,
      };
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to present paywall:', error);
      import('@/utils/clarityEvents')
        .then(({ logSubscriptionPurchaseFailed }) =>
          logSubscriptionPurchaseFailed({
            error_code: 'rc_exception',
            from_surface: fromSurface,
          })
        )
        .catch(() => {});
      return { result: PAYWALL_RESULT.ERROR, customerInfo: null };
    }
  }

  /**
   * Present RevenueCat Customer Center
   * Shows a pre-built UI for managing subscriptions, restoring purchases, etc.
   * After Customer Center closes, invalidates cache to ensure fresh data on next fetch.
   */
  async presentCustomerCenter(): Promise<CustomerInfo | null> {
    if (!this.isInitialized) {
      safeLog.warn('⚠️ RevenueCat: Cannot present customer center - SDK not initialized');
      return null;
    }

    try {
      safeLog.info('💰 RevenueCat: Presenting customer center...');
      await RevenueCatUI.presentCustomerCenter();
      safeLog.info('✅ RevenueCat: Customer center closed');
      
      // Invalidate cache to ensure we get fresh data after any changes
      safeLog.info('💰 RevenueCat: Invalidating customer info cache...');
      await Purchases.invalidateCustomerInfoCache();
      
      // Fetch fresh customer info
      const customerInfo = await Purchases.getCustomerInfo();
      safeLog.info('💰 RevenueCat: Fresh customer info after Customer Center', {
        activeSubscriptions: Object.keys(customerInfo.entitlements.active).length,
        activeEntitlements: Object.keys(customerInfo.entitlements.active),
      });
      
      return customerInfo;
    } catch (error) {
      safeLog.error('❌ RevenueCat: Failed to present customer center:', error);
      return null;
    }
  }

  /**
   * Check if user has "Padrinho Plus" entitlement
   * Convenience method for checking the main premium entitlement
   */
  async hasPadrinhoPlus(): Promise<boolean> {
    return this.hasActiveEntitlement('Padrinho Plus');
  }
}

// Export singleton instance
export default new RevenueCatService();
